import { NotifyOnChangeProps, useQuery } from '@tanstack/react-query';
import { useApiClients } from '../ApiClientsProvider';
import { queryClient } from '../query-client';
import MarketApiClient from '../clients/market-api/market-api.client';
import marketApiClient from '../clients/market-api/market-api.client';
import { UnderlyingLastTrade } from '@oai-frontend-monorepo/types';

export const prefetchUnderlyingLastTrade = (symbol: string) => {
  return queryClient.prefetchQuery({
    queryKey: ['underlying-last-trade', symbol],
    queryFn: async () => {
      return MarketApiClient.getUnderlyingLastTrade(symbol);
    },
    staleTime: 10000,
  });
};

type Props = {
  symbols?: string[];
  enabled?: boolean;
  refetchInterval?: number;
  useArraySelector?: boolean;
  notifyOnChangeProps?: NotifyOnChangeProps;
};

export const useUnderlyingLastTrades = ({
  symbols = [],
  useArraySelector,
  refetchInterval = 10000,
}: Props) => {
  return useQuery({
    queryKey: ['underlying-last-trade', ...symbols],
    queryFn: async () => {
      if (!symbols.length) {
        throw new Error('No symbols provided');
      }

      const res = await marketApiClient.getUnderlyingLastTrades(symbols);

      res.forEach((item) => {
        queryClient.setQueryData(['underlying-last-trade', item.symbol], item);

        queryClient.setQueryData<UnderlyingLastTrade>(
          ['underlying-last-trade', item.symbol, true],
          item
        );
      });

      return symbols.length === 1 ? res?.[0] : res;
    },
    select: useArraySelector
      ? (d) => {
          if (!d) {
            return undefined;
          }

          return Array.isArray(d) ? d : [d];
        }
      : undefined,
    enabled: !!symbols.length,
    refetchInterval,
    staleTime: 10000,
  });
};

type Props2 = {
  symbol?: string;
  enabled?: boolean;
  refetchInterval?: number;
  hasParentQuery?: boolean;
};

export const useUnderlyingLastTrade = ({
  enabled = true,
  hasParentQuery = false,
  refetchInterval = 10000,
  symbol,
}: Props2) => {
  const { marketApi } = useApiClients();

  return useQuery({
    queryKey: hasParentQuery
      ? ['underlying-last-trade', symbol, hasParentQuery]
      : ['underlying-last-trade', symbol],
    queryFn: async () => {
      if (!symbol) {
        throw new Error('No symbol provided');
      }

      if (hasParentQuery) {
        return (
          queryClient.getQueryData<UnderlyingLastTrade>([
            'underlying-last-trade',
            symbol,
          ]) || null
        );
      }

      return marketApi.getUnderlyingLastTrade(symbol);
    },
    enabled: enabled && !!symbol,
    refetchInterval,
    staleTime: 10000,
  });
};
